/**
 * RFQModal.tsx — "Prepara RFQ" modal. Product decision (2026-07-25): the app
 * does NOT send email; it PREPARES a price-scrubbed package the user downloads
 * and emails themselves. Left = supplier picker (pre-selecting suppliers whose
 * categories overlap this category); right = subject + body prefilled from the
 * REAL project + category names. "Genera pacchetto RFQ" creates the RFQ, fetches
 * its presigned package URL and triggers the download, then shows a done state.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { I } from "../../components/icons";
import { apiErrorMessage } from "../../lib/api";
import {
  SUPPLIER_TYPE_LABEL,
  useCreateRfq,
  useRfqPackageUrl,
  useSuppliers,
  type Supplier,
} from "../../lib/strategyApi";

function Modal({
  onClose,
  width = 780,
  children,
}: {
  onClose: () => void;
  width?: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,28,0.45)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        style={{
          width,
          maxWidth: "100%",
          maxHeight: "85vh",
          background: "var(--c-bg-elev)",
          borderRadius: 12,
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function overlaps(supplier: Supplier, categoryName: string): boolean {
  const cat = categoryName.trim().toLowerCase();
  if (!cat) return false;
  return supplier.categories.some((c) => {
    const s = c.trim().toLowerCase();
    return s.length > 0 && (s.includes(cat) || cat.includes(s));
  });
}

function defaultBody(projectName: string, categoryName: string): string {
  return `Gentile fornitore,

richiediamo una vostra migliore offerta per le opere della categoria "${categoryName}" relative al progetto "${projectName}".

In allegato trovate il computo metrico estratto (con i prezzi unitari oscurati) e la documentazione tecnica pertinente.

Vi chiediamo di indicare:
• importi separati per materiali e mano d'opera;
• eventuali sconti applicabili;
• tempi di consegna e di posa.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti`;
}

interface RFQModalProps {
  projectId: string;
  projectName: string;
  category: { id: string; name: string };
  onClose: () => void;
}

export function RFQModal({
  projectId,
  projectName,
  category,
  onClose,
}: RFQModalProps): JSX.Element {
  const suppliersQuery = useSuppliers();
  const createRfq = useCreateRfq(projectId);
  const packageUrl = useRfqPackageUrl();

  const suppliers = useMemo(() => suppliersQuery.data ?? [], [suppliersQuery.data]);

  // Pre-select category-matching suppliers once the list first arrives; after
  // that the user's toggles own the selection.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (selected === null && suppliersQuery.data) {
      setSelected(new Set(suppliers.filter((s) => overlaps(s, category.name)).map((s) => s.id)));
    }
  }, [suppliersQuery.data, suppliers, selected, category.name]);
  const sel = selected ?? new Set<string>();

  const [subject, setSubject] = useState(`Richiesta offerta — ${category.name} — ${projectName}`);
  const [body, setBody] = useState(() => defaultBody(projectName, category.name));
  const [hideUnitPrices, setHideUnitPrices] = useState(true);
  const [includeDrawings, setIncludeDrawings] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = createRfq.isPending || packageUrl.isPending;

  function toggle(id: string): void {
    setSelected((cur) => {
      const n = new Set(cur ?? []);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function triggerDownload(url: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function generate(): void {
    setError(null);
    createRfq.mutate(
      {
        categoryId: category.id,
        recipientSupplierIds: [...sel],
        subject,
        body,
        hideUnitPrices,
        includeDrawings,
      },
      {
        onSuccess: (rfq) => {
          packageUrl.mutate(rfq.id, {
            onSuccess: (url) => {
              triggerDownload(url);
              setDone(true);
            },
            onError: (e) => setError(apiErrorMessage(e)),
          });
        },
        onError: (e) => setError(apiErrorMessage(e)),
      },
    );
  }

  if (done) {
    return (
      <Modal onClose={onClose} width={520}>
        <div style={{ padding: 36, textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              background: "var(--c-ok-soft)",
              color: "var(--c-ok)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 14,
            }}
          >
            <I.Check size={26} />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Pacchetto pronto — scaricato</h2>
          <p style={{ color: "var(--c-ink-3)", marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
            Invialo ai {sel.size} fornitori selezionati dalla tua email. Le offerte che riceverai
            potrai caricarle nel tab &quot;Inbox offerte&quot;.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onClose}>
            Chiudi
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} width={780}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--c-line)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "var(--c-brand-soft)",
            color: "var(--c-brand)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Send size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Prepara RFQ — {category.name}</div>
          <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>{projectName}</div>
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
          <I.X size={14} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", maxHeight: "68vh" }}>
        {/* Suppliers */}
        <div
          style={{
            borderRight: "1px solid var(--c-line)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--c-line)",
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--c-ink-3)",
            }}
          >
            Destinatari ({sel.size})
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {suppliersQuery.isLoading ? (
              <div style={{ padding: 14, fontSize: 12, color: "var(--c-ink-3)" }}>
                Caricamento fornitori…
              </div>
            ) : suppliers.length === 0 ? (
              <div style={{ padding: 14, fontSize: 12, color: "var(--c-ink-3)" }}>
                Nessun fornitore in anagrafica.
              </div>
            ) : (
              suppliers.map((s) => {
                const match = overlaps(s, category.name);
                return (
                  <label
                    key={s.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderBottom: "1px solid var(--c-line)",
                      cursor: "pointer",
                      background: sel.has(s.id) ? "var(--c-brand-soft)" : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sel.has(s.id)}
                      onChange={() => toggle(s.id)}
                      style={{ accentColor: "var(--c-brand)" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                          style={{
                            fontSize: 12.5,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {s.name}
                        </span>
                        {match && (
                          <span className="pill pill-brand" style={{ height: 14, fontSize: 9 }}>
                            categoria
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--c-ink-3)" }}>
                        {SUPPLIER_TYPE_LABEL[s.supplierType]} · {s.quoteCount} offerte
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Composer */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto" }}>
          <div
            style={{
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              borderBottom: "1px solid var(--c-line)",
            }}
          >
            <div>
              <label style={{ fontSize: 11.5, color: "var(--c-ink-3)", fontWeight: 500 }}>
                Oggetto
              </label>
              <input
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              style={{
                width: "100%",
                minHeight: 190,
                padding: 12,
                fontSize: 12.5,
                fontFamily: "inherit",
                border: "1px solid var(--c-line-strong)",
                borderRadius: 6,
                resize: "vertical",
                lineHeight: 1.55,
                background: "var(--c-bg-elev)",
                color: "var(--c-ink)",
              }}
            />
          </div>

          {/* Package options */}
          <div style={{ padding: 16, borderBottom: "1px solid var(--c-line)" }}>
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--c-ink-3)",
                marginBottom: 8,
              }}
            >
              Contenuto del pacchetto
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={hideUnitPrices}
                  onChange={(e) => setHideUnitPrices(e.target.checked)}
                  style={{ accentColor: "var(--c-brand)" }}
                />
                Nascondi prezzi unitari
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={includeDrawings}
                  onChange={(e) => setIncludeDrawings(e.target.checked)}
                  style={{ accentColor: "var(--c-brand)" }}
                />
                Includi disegni tecnici
              </label>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                padding: "8px 10px",
                background: "var(--c-bg-sunken)",
                borderRadius: 6,
                fontSize: 11.5,
                color: "var(--c-ink-3)",
                lineHeight: 1.5,
              }}
            >
              <I.Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                {
                  "L'app prepara un pacchetto con i prezzi oscurati, pronto da scaricare: sarai tu a inviarlo ai fornitori dalla tua email."
                }
              </span>
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "10px 16px",
                fontSize: 12,
                color: "var(--c-err)",
                background: "var(--c-err-soft)",
                borderBottom: "1px solid var(--c-line)",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
              {sel.size} destinatari selezionati
            </span>
            <div style={{ flex: 1 }} />
            <button className="btn btn-sm" onClick={onClose}>
              Annulla
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={generate}
              disabled={sel.size === 0 || busy}
            >
              <I.Download size={12} /> {busy ? "Generazione…" : "Genera pacchetto RFQ"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
